<template>
  <div class="global-search" ref="referenceRef">
    <!-- Search Input -->
    <div class="field">
      <div class="control has-icons-left">
        <input
          ref="searchInput"
          v-model="searchQuery"
          class="input"
          type="text"
          placeholder="Search beers, breweries, pubs, events, sessions, and styles..."
          @focus="handleFocus"
          @blur="handleBlur"
          @keydown="handleKeydown"
          @input="handleInput"
          :class="{ 'is-loading': isLoading }"
          data-testid="global-search-input"
        />
        <span class="icon is-left">
          <i class="fas fa-search"></i>
        </span>
      </div>
    </div>

    <!-- Floating Search Results -->
    <Teleport to="body">
      <div
        v-if="showDropdown"
        ref="floatingRef"
        :style="floatingStyles"
        class="search-dropdown"
        data-testid="global-search-dropdown"
      >
        <!-- Loading State -->
        <div v-if="isLoading" class="search-loading">
          <div class="has-text-centered py-4">
            <span class="icon">
              <i class="fas fa-spinner fa-spin"></i>
            </span>
            <span class="ml-2">Searching...</span>
          </div>
        </div>

        <!-- Search Results -->
        <div v-else-if="searchResults.length > 0" class="search-results">
          <div class="search-results-header">
            <span class="results-count">
              {{ searchResults.length }} result{{
                searchResults.length === 1 ? '' : 's'
              }}
              {{ searchQuery ? `for "${searchQuery}"` : '' }}
            </span>
          </div>

          <div class="search-results-list">
            <SearchResultItem
              v-for="(result, index) in searchResults"
              :key="`${result.type}-${result.id}`"
              :result="result"
              :search-query="searchQuery"
              :is-focused="focusedIndex === index"
              :index="index"
              @focus="setFocusedIndex"
              @select="selectResult"
            />
          </div>

          <div v-if="hasMoreResults" class="search-footer">
            <div class="has-text-centered py-2">
              <span class="is-size-7 has-text-grey">
                Showing first {{ searchResults.length }} results
              </span>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="searchQuery.length >= 2" class="search-empty">
          <div class="has-text-centered py-4">
            <span class="icon is-large has-text-grey-lighter">
              <i class="fas fa-search fa-2x"></i>
            </span>
            <p class="has-text-grey">
              No results found for "<strong>{{ searchQuery }}</strong
              >"
            </p>
            <p class="is-size-7 has-text-grey-light mt-2">
              Try searching for beer names, breweries, pubs, events, or sessions
            </p>
          </div>
        </div>

        <!-- Initial State / Recent Searches -->
        <div v-else-if="recentSearches.length > 0" class="search-recent">
          <div class="search-results-header">
            <span class="results-count">Recent searches</span>
            <button
              class="button is-small is-text"
              @click="clearRecentSearches"
            >
              Clear
            </button>
          </div>

          <div class="search-results-list">
            <div
              v-for="term in recentSearches"
              :key="term"
              class="recent-search-item"
              @click="performSearch(term)"
            >
              <span class="icon is-small has-text-grey">
                <i class="fas fa-history"></i>
              </span>
              <span>{{ term }}</span>
            </div>
          </div>
        </div>

        <!-- Help Text for Empty Input -->
        <div v-else class="search-help">
          <div class="has-text-centered py-4">
            <p class="has-text-grey">
              Start typing to search beers, breweries, pubs, events, and sessions
            </p>
            <p class="is-size-7 has-text-grey-light mt-2">
              <kbd>Ctrl</kbd> + <kbd>K</kbd> to focus search
            </p>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/vue'
import { watchDebounced } from '@vueuse/core'
import { onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import SearchResultItem from './SearchResultItem.vue'

interface SearchResult {
  type: string
  id: string
  name: string
  subtitle: string
  url: string
  meta?: any
}

interface SearchResponse {
  results: {
    beers: SearchResult[]
    breweries: SearchResult[]
    pubs: SearchResult[]
    events: SearchResult[]
    sessions: SearchResult[]
    styles: SearchResult[]
  }
  total: number
  query: string
}

// Template refs
const referenceRef = ref<HTMLElement>()
const floatingRef = ref<HTMLElement>()
const searchInput = ref<HTMLInputElement>()

// Router
const router = useRouter()

// Search state
const searchQuery = ref('')
const searchResults = ref<SearchResult[]>([])
const isLoading = ref(false)
const showDropdown = ref(false)
const focusedIndex = ref(-1)
const hasMoreResults = ref(false)
const recentSearches = ref<string[]>([])

// Floating UI setup
const { floatingStyles } = useFloating(referenceRef, floatingRef, {
  placement: 'bottom-start',
  strategy: 'fixed',
  middleware: [
    offset(5),
    flip(),
    shift({ padding: 10 }),
    size({
      apply({ availableWidth, availableHeight, elements }) {
        // Ensure dropdown doesn't exceed viewport
        Object.assign(elements.floating.style, {
          maxWidth: `${Math.min(availableWidth, 600)}px`,
          maxHeight: `${Math.min(availableHeight - 100, 400)}px`,
          minWidth: '300px',
        })
      },
    }),
  ],
  whileElementsMounted: autoUpdate,
})

// Debounced search
watchDebounced(
  searchQuery,
  async newQuery => {
    if (newQuery.length >= 2) {
      await performSearch(newQuery)
    } else {
      searchResults.value = []
      focusedIndex.value = -1
    }
  },
  { debounce: 300 }
)

// Load recent searches from localStorage
const loadRecentSearches = () => {
  try {
    const stored = localStorage.getItem('beer-search-recent')
    if (stored) {
      recentSearches.value = JSON.parse(stored).slice(0, 5) // Keep only 5 most recent
    }
  } catch (error) {
    console.warn('Failed to load recent searches:', error)
  }
}

// Save recent search
const saveRecentSearch = (query: string) => {
  if (!query || query.length < 2) return

  try {
    const updated = [
      query,
      ...recentSearches.value.filter(term => term !== query),
    ].slice(0, 5)
    recentSearches.value = updated
    localStorage.setItem('beer-search-recent', JSON.stringify(updated))
  } catch (error) {
    console.warn('Failed to save recent search:', error)
  }
}

// Clear recent searches
const clearRecentSearches = () => {
  recentSearches.value = []
  try {
    localStorage.removeItem('beer-search-recent')
  } catch (error) {
    console.warn('Failed to clear recent searches:', error)
  }
}

// Perform search API call
const performSearch = async (query: string) => {
  if (!query || query.length < 2) {
    searchResults.value = []
    return
  }

  isLoading.value = true
  focusedIndex.value = -1

  try {
    const response = await fetch(
      `/api/search?q=${encodeURIComponent(query)}&types=beers,breweries,pubs,events,sessions,styles&limit=8`
    )

    if (!response.ok) {
      throw new Error(`Search failed: ${response.statusText}`)
    }

    const data: SearchResponse = await response.json()

    // Combine all result types
    const beers = data.results.beers || []
    const breweries = data.results.breweries || []
    const pubs = data.results.pubs || []
    const events = data.results.events || []
    const sessions = data.results.sessions || []
    const styles = data.results.styles || []
    searchResults.value = [...events, ...sessions, ...breweries, ...pubs, ...styles, ...beers]
    hasMoreResults.value = data.total > searchResults.value.length

    // Save to recent searches if this was a manual search
    if (query === searchQuery.value) {
      saveRecentSearch(query)
    } else {
      // if this was triggered by clicking a recent search, make sure the dropdown stays open
      showDropdown.value = true
    }
  } catch (error) {
    console.error('Search error:', error)
    searchResults.value = []
    hasMoreResults.value = false
  } finally {
    isLoading.value = false
  }
}

// Event handlers
const handleFocus = () => {
  showDropdown.value = true
  focusedIndex.value = -1
}

const handleBlur = () => {
  // Delay hiding dropdown to allow clicks on results
  setTimeout(() => {
    showDropdown.value = false
  }, 150)
}

const handleInput = () => {
  focusedIndex.value = -1
  if (!showDropdown.value) {
    showDropdown.value = true
  }
}

const handleKeydown = (event: KeyboardEvent) => {
  if (!showDropdown.value) return

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      focusedIndex.value = Math.min(
        focusedIndex.value + 1,
        searchResults.value.length - 1
      )
      break

    case 'ArrowUp':
      event.preventDefault()
      focusedIndex.value = Math.max(focusedIndex.value - 1, -1)
      break

    case 'Enter':
      event.preventDefault()
      if (focusedIndex.value >= 0 && searchResults.value[focusedIndex.value]) {
        selectResult(searchResults.value[focusedIndex.value])
      }
      break

    case 'Escape':
      event.preventDefault()
      hideDropdown()
      break
  }
}

const setFocusedIndex = (index: number) => {
  focusedIndex.value = index
}

const selectResult = (result: SearchResult) => {
  hideDropdown()
  searchInput.value?.blur()

  // Navigate to result
  if (result.url) {
    router.push(result.url)
  }
}

const hideDropdown = () => {
  showDropdown.value = false
  focusedIndex.value = -1
}

// Focus search with keyboard shortcut
const handleGlobalKeydown = (event: KeyboardEvent) => {
  if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
    event.preventDefault()
    searchInput.value?.focus()
  }
}

// Expose focus method for external use
const focus = () => {
  searchInput.value?.focus()
}

defineExpose({
  focus,
})

// Lifecycle
onMounted(() => {
  loadRecentSearches()
  document.addEventListener('keydown', handleGlobalKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeydown)
})
</script>

<style scoped>
.global-search {
  position: relative;
}

.search-dropdown {
  background: white;
  border: 1px solid #dbdbdb;
  border-radius: 6px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  z-index: 9999;
}

.search-results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.results-count {
  font-size: 0.8rem;
  font-weight: 600;
  color: #495057;
}

.search-results-list {
  max-height: 300px;
  overflow-y: auto;
}

.search-loading,
.search-empty,
.search-help {
  padding: 1rem;
  color: #6c757d;
}

.search-footer {
  border-top: 1px solid #f5f5f5;
  background-color: #fafafa;
}

.recent-search-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.recent-search-item:hover {
  background-color: #f8f9fa;
}

kbd {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 3px;
  color: #495057;
  font-size: 0.75rem;
  padding: 0.1rem 0.3rem;
}

/* Custom scrollbar for results */
.search-results-list::-webkit-scrollbar {
  width: 6px;
}

.search-results-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.search-results-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.search-results-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
